import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, MapPin, ClockCounterClockwise, ArrowRight } from "@phosphor-icons/react";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

const daysBetween = (a, b) => {
  if (!a || !b) return null;
  try {
    return Math.max(1, Math.round((new Date(a) - new Date(b)) / 86400000));
  } catch { return null; }
};

export default function CameraHistoryModal({ open, onClose, camera }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !camera?.id) return;
    setLoading(true);
    api.get(`/security-fleet/camera/${camera.id}/history`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, camera?.id]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="camera-history-modal" onClick={onClose}>
      <div className="bg-white border-4 border-[#0f172a] max-w-2xl w-full my-8 shadow-[8px_8px_0px_#000]"
        onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-slate-900 flex items-center justify-between bg-cyan-100">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800">Move History</div>
            <h2 className="font-display font-black text-2xl">
              {camera?.code || "Camera"}
            </h2>
            <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
              <MapPin size={12} weight="fill"/>
              {data?.current_address || camera?.address || <span className="italic">not deployed</span>}
              {data?.current_unit && ` · Unit ${data.current_unit}`}
            </div>
          </div>
          <button onClick={onClose} data-testid="close-history"
            className="w-10 h-10 border-2 border-slate-900 bg-white hover:bg-red-100 flex items-center justify-center">
            <X weight="bold" size={18}/>
          </button>
        </div>

        <div className="p-4">
          {loading && <div className="text-center py-8 text-sm text-slate-500 animate-pulse">Loading timeline…</div>}
          {!loading && data && data.moves.length === 0 && (
            <div className="p-4 bg-slate-50 border-2 border-dashed border-slate-300 text-center">
              <ClockCounterClockwise size={28} className="mx-auto text-slate-400"/>
              <div className="mt-2 text-sm font-black uppercase tracking-widest text-slate-600">Never moved</div>
              <div className="text-xs text-slate-500 mt-1">
                First deployed {fmt(data.first_deployed_at)}
              </div>
            </div>
          )}

          {!loading && data && data.moves.length > 0 && (
            <div className="space-y-3" data-testid="history-timeline">
              {/* Current location card */}
              <div className="p-3 border-2 border-emerald-600 bg-emerald-50 shadow-[3px_3px_0px_#065f46]">
                <div className="text-[9px] font-black uppercase tracking-widest text-emerald-700">Right now</div>
                <div className="font-black text-sm mt-0.5">
                  {data.current_address || <span className="italic text-slate-500">not deployed</span>}
                  {data.current_unit && <span className="text-slate-500 font-normal"> · Unit {data.current_unit}</span>}
                </div>
                <div className="text-[10px] text-slate-600 mt-0.5">Since {fmt(data.last_deployed_at)}</div>
              </div>

              {/* Each move row */}
              {data.moves.map((m, i) => {
                const nextTs = i === 0 ? data.last_deployed_at : data.moves[i - 1].moved_at;
                const stayDays = daysBetween(nextTs, m.moved_at);
                return (
                  <div key={m.id || i} className="p-3 border-2 border-slate-300 bg-white"
                    data-testid={`history-row-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap text-sm">
                      <span className="font-black text-slate-700">
                        {m.from_address || <span className="italic text-slate-400">warehouse</span>}
                      </span>
                      {m.from_unit && <span className="text-slate-500">·{m.from_unit}</span>}
                      <ArrowRight weight="bold" size={14} className="text-cyan-600"/>
                      <span className={`font-black ${m.to_address ? "text-slate-900" : "text-red-700"}`}>
                        {m.to_address || "OFF-LINE / UN-DEPLOYED"}
                      </span>
                      {m.to_unit && <span className="text-slate-500">·{m.to_unit}</span>}
                    </div>
                    <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2 flex-wrap">
                      <span>{fmt(m.moved_at)}</span>
                      <span>·</span>
                      <span>by {m.moved_by || "system"}</span>
                      {m.reason && m.reason !== "manual_move" && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-500 rounded-none">
                          {m.reason.replace(/_/g, " ")}
                        </span>
                      )}
                      {stayDays !== null && (
                        <span className="ml-auto text-[9px] italic text-slate-400 normal-case">
                          stayed {stayDays} day{stayDays === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
